import { PublicMetadata } from './components/PublicMetadata';
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { createContext, useContext, useState, useEffect, ReactNode, Suspense, lazy, Component } from "react";

import AppLayout, { PageSkeleton } from "./components/layout/AppLayout";
import AgenticOSDemoShell from "./components/demos/AgenticOSDemoShell";
import { SessionProvider } from "./lib/session";
import { DEMOS } from "./data/demos";
import Explore from "./pages/Explore";
import DemoDetail from "./pages/DemoDetail";
import InteractiveDemo from "./pages/InteractiveDemo";
import { resolveOperator } from "@/src/data/operators";
import { operatorSlugFromHostname } from "@/src/lib/operator";

// Heavy/non-critical pages stay lazy-loaded. The buyer-facing Explore and demo
// routes are eager so public launch never gets trapped behind a Suspense shell.
const Build = lazy(() => import("./pages/Build"));
const Intelligence = lazy(() => import("./pages/Intelligence"));
const MyOS = lazy(() => import("./pages/MyOS"));
const EmbedDemo = lazy(() => import("./pages/EmbedDemo"));
const LiveOS = lazy(() => import("./pages/LiveOS"));
const ApplyPage = lazy(() => import("./pages/ApplyPage"));

// Per-route error boundary — catches crashes inside lazy-loaded pages
// so one broken route can't blank out the whole app.
interface RouteErrorBoundaryProps {
  children: ReactNode;
  name?: string;
}
interface RouteErrorBoundaryState {
  error: Error | null;
}
class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error(`[RouteErrorBoundary:${this.props.name}]`, error);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-500 p-8">
          <div className="text-4xl">⚠️</div>
          <p className="font-semibold text-slate-900">This page ran into a problem</p>
          <p className="text-sm text-center max-w-sm">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ error: null })}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-700 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SafeRoute({ children, name }: { children: ReactNode; name: string }) {
  return (
    <RouteErrorBoundary name={name}>
      {children}
    </RouteErrorBoundary>
  );
}

function RootGate() {
  const [searchParams] = useSearchParams();
  const embedId = searchParams.get("embed");

  if (embedId) {
    const demo = DEMOS.find((item) => item.id === embedId);
    if (demo) {
      return (
        <SafeRoute name="EmbedDemo">
          <AgenticOSDemoShell demo={demo} embedded />
        </SafeRoute>
      );
    }
  }

  return <AppLayout />;
}

// ═══════════════════════════════════════════════════════════════════════
// ITERATION 4: CROSS-DEMO SESSION MEMORY
// ═══════════════════════════════════════════════════════════════════════
//
// Tracks which demo pages the prospect has visited during this browser
// session. If they explore multiple demos before clicking "Build mine",
// the Architect receives the list and can ask contextual questions like
// "I noticed you explored both Realty and Property Management — are you
// running both sides of the business?"
//
// Persistence: sessionStorage (per-tab, cleared when tab closes).
// Capacity: last 10 visits (deduped — recent unique demos kept).
// ═══════════════════════════════════════════════════════════════════════



export default function App() {
  const hostname = window.location.hostname;

  // Detect if we are on a deployed Operating System subdomain
  // This matches *.sloelabs.com or *.localhost (except 'studio' or 'www' or root)
  let subdomain: string | null = null;
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    // If localhost, parts[0] is the subdomain if parts.length > 1
    if (hostname.includes("localhost") && parts.length > 1 && parts[0] !== "localhost") {
      subdomain = parts[0];
    }
    // If production, e.g. foo.sloelabs.com
    else if (
      parts.length >= 3 &&
      parts[0] !== "studio" &&
      parts[0] !== "www" &&
      !hostname.endsWith("vercel.app") &&
      // Approved-operator slugs (<slug>.sloelabs.com) render the operator door
      // (Explore/AppLayout), NOT LiveOS — keep subdomain null so they fall
      // through to the Architect Builder branch below. Non-operator slugs still
      // set subdomain → LiveOS.
      !operatorSlugFromHostname()
    ) {
      subdomain = parts[0];
    }
  }

  // If a subdomain is detected, hijack the entire React tree and render the LiveOS
  if (subdomain) {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <LiveOS subdomain={subdomain} />
      </Suspense>
    );
  }

  // Otherwise, render the standard Sloe Labs Architect Builder
  return (
    <SessionProvider>
      <BrowserRouter>
        <PublicMetadata />
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Embed route: OUTSIDE AppLayout — no sidebar. For iframe previews. */}
            <Route path="embed/:demoId" element={<SafeRoute name="EmbedDemo"><EmbedDemo /></SafeRoute>} />
            {/* Public operator-application funnel — OUTSIDE AppLayout (no Studio sidebar). */}
            <Route path="apply" element={<SafeRoute name="Apply"><ApplyPage /></SafeRoute>} />

            <Route path="/" element={<RootGate />}>
              <Route index element={<SafeRoute name="Explore"><Explore /></SafeRoute>} />
              <Route path="build" element={<SafeRoute name="Build"><Build /></SafeRoute>} />
              <Route path="build/:buildId" element={<SafeRoute name="Build"><Build /></SafeRoute>} />
              <Route path="sync" element={<SafeRoute name="Intelligence"><Intelligence /></SafeRoute>} />
              <Route path="vault" element={<SafeRoute name="MyOS"><MyOS /></SafeRoute>} />
              <Route path="demo/:demoId" element={<SafeRoute name="DemoDetail"><DemoDetail /></SafeRoute>} />
              <Route path="explore/:slug" element={<SafeRoute name="InteractiveDemo"><InteractiveDemo /></SafeRoute>} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </SessionProvider>
  );
}
